import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Switch,
  FormControlLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import { firestore } from '../../firebase/config';
import { collection, addDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  datePicker: {
    width: '100%',
  }
}));

const CouponCreatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderValue: '0',
    maxDiscountAmount: '0',
    usageLimit: '0',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    status: 'active'
  });
  
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked ? 'active' : 'disabled'
    });
  };

  const handleCancel = () => {
    navigate('/admin/coupons');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Le code du coupon est requis';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.code)) {
      newErrors.code = 'Le code doit contenir uniquement des lettres majuscules, chiffres, tirets et underscores';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (!formData.discountValue) {
      newErrors.discountValue = 'La valeur de la réduction est requise';
    } else if (isNaN(formData.discountValue) || parseFloat(formData.discountValue) <= 0) {
      newErrors.discountValue = 'La valeur doit être un nombre positif';
    } else if (formData.discountType === 'percentage' && parseFloat(formData.discountValue) > 100) {
      newErrors.discountValue = 'Le pourcentage ne peut pas dépasser 100%';
    }
    
    if (formData.minOrderValue && (isNaN(formData.minOrderValue) || parseFloat(formData.minOrderValue) < 0)) {
      newErrors.minOrderValue = 'La valeur minimale doit être un nombre positif ou zéro';
    }
    
    if (formData.maxDiscountAmount && (isNaN(formData.maxDiscountAmount) || parseFloat(formData.maxDiscountAmount) < 0)) {
      newErrors.maxDiscountAmount = 'Le montant maximum doit être un nombre positif ou zéro';
    }
    
    if (formData.usageLimit && (isNaN(formData.usageLimit) || parseInt(formData.usageLimit) < 0)) {
      newErrors.usageLimit = 'La limite d\'utilisation doit être un nombre entier positif ou zéro';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'La date de début est requise';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'La date de fin est requise';
    } else if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'La date de fin doit être postérieure à la date de début';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Préparer les données pour l'envoi
      const couponData = {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        minOrderValue: parseFloat(formData.minOrderValue) || 0,
        maxDiscountAmount: parseFloat(formData.maxDiscountAmount) || 0,
        usageLimit: parseInt(formData.usageLimit) || 0,
        usageCount: 0,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Simulation d'ajout dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const couponsCollection = collection(firestore, 'coupons');
      await addDoc(couponsCollection, couponData);
      
      setSnackbar({
        open: true,
        message: 'Coupon créé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste des coupons après un court délai
      setTimeout(() => {
        navigate('/admin/coupons');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating coupon:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la création du coupon',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Créer un nouveau coupon
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="code"
                  label="Code du coupon"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.code}
                  onChange={handleChange}
                  error={!!errors.code}
                  helperText={errors.code || "Ex: SUMMER25, WELCOME10 (lettres majuscules, chiffres, tirets et underscores)"}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.status}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status === 'active'}
                        onChange={handleSwitchChange}
                        name="status"
                        color="primary"
                      />
                    }
                    label="Coupon actif"
                  />
                  {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.discountType}>
                  <InputLabel id="discount-type-label">Type de réduction</InputLabel>
                  <Select
                    labelId="discount-type-label"
                    id="discountType"
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleChange}
                    label="Type de réduction"
                  >
                    <MenuItem value="percentage">Pourcentage (%)</MenuItem>
                    <MenuItem value="fixed">Montant fixe (€)</MenuItem>
                  </Select>
                  {errors.discountType && <FormHelperText>{errors.discountType}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="discountValue"
                  label="Valeur de la réduction"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.discountValue}
                  onChange={handleChange}
                  error={!!errors.discountValue}
                  helperText={errors.discountValue}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.discountType === 'percentage' ? '%' : '€'}
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                    max: formData.discountType === 'percentage' ? 100 : undefined,
                    step: formData.discountType === 'percentage' ? 1 : 0.01
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="minOrderValue"
                  label="Valeur minimale de commande"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={formData.minOrderValue}
                  onChange={handleChange}
                  error={!!errors.minOrderValue}
                  helperText={errors.minOrderValue || "Montant minimum pour appliquer le coupon (0 = pas de minimum)"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="maxDiscountAmount"
                  label="Montant maximum de réduction"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={formData.maxDiscountAmount}
                  onChange={handleChange}
                  error={!!errors.maxDiscountAmount}
                  helperText={errors.maxDiscountAmount || "Montant maximum de la réduction (0 = pas de maximum)"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01
                  }}
                  disabled={formData.discountType === 'fixed'}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="usageLimit"
                  label="Limite d'utilisation"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={formData.usageLimit}
                  onChange={handleChange}
                  error={!!errors.usageLimit}
                  helperText={errors.usageLimit || "Nombre maximum d'utilisations (0 = illimité)"}
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="startDate"
                  label="Date de début"
                  type="date"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.startDate}
                  onChange={handleChange}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  className={classes.datePicker}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="endDate"
                  label="Date de fin"
                  type="date"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.endDate}
                  onChange={handleChange}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  className={classes.datePicker}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
              >
                {submitting ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponCreatePage;
